import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { isEmpty } from 'lodash';

import {
  createNewAssignment,
  createNewAssignmentFromDraft,
} from './assignmentSlice';
import backofficeApiClient, {
  AssignmentTypeEnum,
  AssignmentPrimaryDocumentEditModel,
} from 'api/backofficeApiClient';
import adminApiClient from 'api/adminApiClient';

interface ContractorCompanyState {
  contractorCompanyCreatedAt?: number;
}

const initialState: ContractorCompanyState = {
  contractorCompanyCreatedAt: undefined,
};

const getContractorCompaniesForCurrentMember = createAsyncThunk(
  'contractorCompany/getContractorCompaniesForCurrentMember',
  async (pageNumber: number) => {
    return backofficeApiClient.getContractorCompaniesPagedListForCurrentMember(
      pageNumber,
    );
  },
);

interface ContractorCompanyRequestModel {
  requestId: number;
  pageNumber: number;
  searchQuery?: string;
  companyIds?: string[];
}
const getContractorCompaniesParentSCAgents = createAsyncThunk(
  'contractorCompany/getContractorCompaniesParentSCAgents',
  async ({
    pageNumber,
    searchQuery,
    companyIds,
  }: ContractorCompanyRequestModel) => {
    return adminApiClient.getContractorCompaniesPagedList(
      pageNumber,
      searchQuery,
      companyIds,
    );
  },
);

const getContractorCompanyCompanies = createAsyncThunk(
  'contractorCompany/getContractorCompanyCompanies',
  async () => {
    return adminApiClient.getMembersWithContractorCompany();
  },
);

interface MemberCompaniesRequestModel {
  isAdmin?: boolean;
  companyId: string;
}
const getMemberCompanies = createAsyncThunk(
  'contractorCompany/getMemberCompanies',
  async ({ isAdmin, companyId }: MemberCompaniesRequestModel) => {
    return isAdmin
      ? adminApiClient.getContractorCompaniesForMember(companyId)
      : backofficeApiClient.getContractorCompaniesForCurrentMember();
  },
);

const isCompanyCreatedFromAssignment = (
  assignmentData: AssignmentPrimaryDocumentEditModel,
): boolean => {
  const { contractorCompanyInfo, assignmentType } = assignmentData;

  return (
    assignmentType !== AssignmentTypeEnum.W2Assignment &&
    !isEmpty(contractorCompanyInfo) &&
    !contractorCompanyInfo?.companyId
  );
};

interface DownloadContractorCompanyFileRequest {
  companyId: string;
  isAdminRole: boolean;
}
const getContractorCompanyInfoFile = createAsyncThunk(
  'contractorCompany/getContractorCompanyInfoFile',
  async ({ companyId, isAdminRole }: DownloadContractorCompanyFileRequest) => {
    return isAdminRole
      ? adminApiClient.getContractorCompanyFile(companyId)
      : backofficeApiClient.getContractorCompanyFile(companyId);
  },
);

const getHistoryContractorCompaniesFile = createAsyncThunk(
  'contractorCompany/getHistoryContractorCompaniesFile',
  async ({ companyId, fileKey }: { companyId?: string; fileKey?: string }) => {
    return adminApiClient.getHistoryContractorCompaniesFile(companyId, fileKey);
  },
);

const contractorCompanySlice = createSlice({
  name: 'contractorCompany',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(createNewAssignment.fulfilled, (state, action) => {
      const {
        meta: { arg },
      } = action;

      isCompanyCreatedFromAssignment(arg) &&
        (state.contractorCompanyCreatedAt = Date.now());
    });
    builder.addCase(createNewAssignmentFromDraft.fulfilled, (state, action) => {
      const {
        meta: { arg },
      } = action;

      isCompanyCreatedFromAssignment(arg.data) &&
        (state.contractorCompanyCreatedAt = Date.now());
    });
  },
});

const { reducer } = contractorCompanySlice;

export {
  getMemberCompanies,
  getContractorCompanyInfoFile,
  getContractorCompanyCompanies,
  getContractorCompaniesParentSCAgents,
  getContractorCompaniesForCurrentMember,
  getHistoryContractorCompaniesFile,
};
export default reducer;
